/**
 * Phase 2G.9 - operational loss prevention & control review.
 *
 * A control review case is an ADMINISTRATOR-DRIVEN investigation record
 * around an existing source of truth (a transaction, a reconciliation, an
 * inventory item, a purchase/expense document, a handover). It is NOT an
 * automated verdict: an exception, discrepancy, shortage or unusual flag
 * is a reason to review the evidence, never proof of theft or misconduct.
 *
 * Honest boundaries (stated, not hidden):
 *   - the case stores a REFERENCE to the source record plus an immutable
 *     snapshot of the factual reason it was flagged; current details are
 *     always derived live from the original module, never copied twice,
 *   - priority is a deterministic property of the CATEGORY's evidence
 *     class (see CONTROL_REVIEW_CATEGORY_PRIORITY), computed server-side
 *     from recorded facts - it is a review-ordering aid, never a finding
 *     of wrongdoing, and no clock, deadline or SLA is involved,
 *   - resolving a case NEVER mutates the underlying business record; any
 *     real correction must be made through that module's own authorized,
 *     audited workflow,
 *   - if the source data cannot support a conclusion, the case must say
 *     so explicitly rather than invent an amount or a cause.
 */

/**
 * Case categories, each backed by an existing source of truth:
 *
 *   ITEM_COUNT_DISCREPANCY      a completed processing step recorded
 *                               actual != expected (Phase 2C/2G.5)
 *   OPEN_OR_STALLED_PROCESSING  a processing step was started and never
 *                               finished (Phase 2G.2 open-step detection)
 *   TRANSACTION_WORKFLOW_INCONSISTENCY stored state contradicts an
 *                               enforced workflow invariant (Phase 2G.2 H)
 *   PAYMENT_COLLECTION_ISSUE    recorded payment facts disagree with the
 *                               transaction total or release snapshot
 *                               (Phase 2G.6 flags)
 *   CASH_RECONCILIATION_DIFFERENCE recorded collections differ from the
 *                               counted/verified actuals (Phase 2G.7)
 *   INVENTORY_ADJUSTMENT_CONCERN an adjustment movement or a low-stock
 *                               balance worth reviewing (Phase 2E)
 *   PURCHASE_OR_EXPENSE_REVIEW  a purchase or expense document selected
 *                               for review (Phase 2E documents)
 *   OPERATIONAL_HANDOVER_ISSUE  a handover carried unresolved items or
 *                               its snapshot disagrees with current state
 *                               (Phase 2G.8)
 *   PICKUP_DELIVERY_EXCEPTION   ready pickup not released / delivery
 *                               workflow left open (Phase 2D/2F)
 */
export const CONTROL_REVIEW_CATEGORIES = [
  'ITEM_COUNT_DISCREPANCY',
  'OPEN_OR_STALLED_PROCESSING',
  'TRANSACTION_WORKFLOW_INCONSISTENCY',
  'PAYMENT_COLLECTION_ISSUE',
  'CASH_RECONCILIATION_DIFFERENCE',
  'INVENTORY_ADJUSTMENT_CONCERN',
  'PURCHASE_OR_EXPENSE_REVIEW',
  'OPERATIONAL_HANDOVER_ISSUE',
  'PICKUP_DELIVERY_EXCEPTION',
] as const;
export type ControlReviewCategory = (typeof CONTROL_REVIEW_CATEGORIES)[number];

export const CONTROL_REVIEW_CATEGORY_LABELS: Record<ControlReviewCategory, string> = {
  ITEM_COUNT_DISCREPANCY: 'Item-count discrepancy',
  OPEN_OR_STALLED_PROCESSING: 'Open or stalled processing',
  TRANSACTION_WORKFLOW_INCONSISTENCY: 'Transaction workflow inconsistency',
  PAYMENT_COLLECTION_ISSUE: 'Recorded payment or collection issue',
  CASH_RECONCILIATION_DIFFERENCE: 'Cash reconciliation difference',
  INVENTORY_ADJUSTMENT_CONCERN: 'Inventory adjustment concern',
  PURCHASE_OR_EXPENSE_REVIEW: 'Purchasing or expense review',
  OPERATIONAL_HANDOVER_ISSUE: 'Operational handover issue',
  PICKUP_DELIVERY_EXCEPTION: 'Pick-up or delivery exception',
};

/** One-line factual explanation of what each category covers. */
export const CONTROL_REVIEW_CATEGORY_HINTS: Record<ControlReviewCategory, string> = {
  ITEM_COUNT_DISCREPANCY:
    'A completed processing step recorded a different actual count than the expected count',
  OPEN_OR_STALLED_PROCESSING: 'A processing step was started but never completed',
  TRANSACTION_WORKFLOW_INCONSISTENCY:
    'Stored transaction state contradicts an enforced workflow rule (possible data problem)',
  PAYMENT_COLLECTION_ISSUE:
    'Recorded payment amounts, method or release snapshot disagree with the transaction total',
  CASH_RECONCILIATION_DIFFERENCE:
    'Recorded collections differ from the counted or verified amounts on a reconciliation',
  INVENTORY_ADJUSTMENT_CONCERN:
    'An adjustment movement or a stock balance at/below its minimum is worth reviewing',
  PURCHASE_OR_EXPENSE_REVIEW: 'A purchase or expense document was selected for review',
  OPERATIONAL_HANDOVER_ISSUE:
    'A handover carried unresolved items, or its snapshot disagrees with the current records',
  PICKUP_DELIVERY_EXCEPTION:
    'A ready pick-up was not released, or the delivery workflow was left open',
};

/**
 * Which source modules a category may be opened from. A case can only be
 * opened against a record type that actually carries the evidence the
 * category describes - the server rejects every other pairing.
 */
export const CONTROL_REVIEW_SOURCE_MODULES = [
  'TRANSACTION',
  'PROCESSING',
  'RECONCILIATION',
  'INVENTORY',
  'PURCHASE',
  'EXPENSE',
  'HANDOVER',
] as const;
export type ControlReviewSourceModule = (typeof CONTROL_REVIEW_SOURCE_MODULES)[number];

export const CONTROL_REVIEW_SOURCE_MODULE_LABELS: Record<ControlReviewSourceModule, string> = {
  TRANSACTION: 'Transaction',
  PROCESSING: 'Processing step',
  RECONCILIATION: 'Cash reconciliation',
  INVENTORY: 'Inventory item',
  PURCHASE: 'Purchase document',
  EXPENSE: 'Expense document',
  HANDOVER: 'Operational handover',
};

/** Allowed (category, source module) pairings - the single source of truth. */
export const CONTROL_REVIEW_CATEGORY_SOURCES: Record<
  ControlReviewCategory,
  readonly ControlReviewSourceModule[]
> = {
  ITEM_COUNT_DISCREPANCY: ['PROCESSING'],
  OPEN_OR_STALLED_PROCESSING: ['PROCESSING'],
  TRANSACTION_WORKFLOW_INCONSISTENCY: ['TRANSACTION'],
  PAYMENT_COLLECTION_ISSUE: ['TRANSACTION'],
  CASH_RECONCILIATION_DIFFERENCE: ['RECONCILIATION'],
  INVENTORY_ADJUSTMENT_CONCERN: ['INVENTORY'],
  PURCHASE_OR_EXPENSE_REVIEW: ['PURCHASE', 'EXPENSE'],
  OPERATIONAL_HANDOVER_ISSUE: ['HANDOVER'],
  PICKUP_DELIVERY_EXCEPTION: ['TRANSACTION'],
};

export function isAllowedCategorySource(
  category: ControlReviewCategory,
  source: ControlReviewSourceModule,
): boolean {
  return (CONTROL_REVIEW_CATEGORY_SOURCES[category] ?? []).includes(source);
}

/**
 * Case lifecycle: OPEN -> UNDER_REVIEW -> ACTION_REQUIRED -> RESOLVED,
 * with a controlled reopen path (RESOLVED -> REOPENED -> UNDER_REVIEW).
 * The reopen keeps the original resolution visible: resolution fields are
 * never cleared, and the reopening is attributed and audited separately.
 */
export const CONTROL_REVIEW_STATUSES = [
  'OPEN',
  'UNDER_REVIEW',
  'ACTION_REQUIRED',
  'RESOLVED',
  'REOPENED',
] as const;
export type ControlReviewStatus = (typeof CONTROL_REVIEW_STATUSES)[number];

export const CONTROL_REVIEW_STATUS_LABELS: Record<ControlReviewStatus, string> = {
  OPEN: 'Open',
  UNDER_REVIEW: 'Under review',
  ACTION_REQUIRED: 'Awaiting action',
  RESOLVED: 'Resolved',
  REOPENED: 'Reopened',
};

/**
 * Valid successors per status. Every transition is performed by its own
 * action endpoint (never a generic status PATCH), so invalid and
 * duplicate transitions are rejected server-side with 409.
 *
 *   OPEN         -> UNDER_REVIEW         (start review)
 *   UNDER_REVIEW -> ACTION_REQUIRED      (record corrective action)
 *   UNDER_REVIEW -> RESOLVED             (resolve with explanation)
 *   ACTION_REQUIRED -> UNDER_REVIEW      (action done, continue review)
 *   ACTION_REQUIRED -> RESOLVED          (resolve with explanation)
 *   RESOLVED     -> REOPENED             (reopen with reason; the
 *                                         original resolution stays visible)
 *   REOPENED     -> UNDER_REVIEW         (resume the review)
 */
export const CONTROL_REVIEW_TRANSITIONS: Record<ControlReviewStatus, ControlReviewStatus[]> = {
  OPEN: ['UNDER_REVIEW'],
  UNDER_REVIEW: ['ACTION_REQUIRED', 'RESOLVED'],
  ACTION_REQUIRED: ['UNDER_REVIEW', 'RESOLVED'],
  RESOLVED: ['REOPENED'],
  REOPENED: ['UNDER_REVIEW'],
};

/** True when from -> to is a legal control-review edge. */
export function isValidControlReviewTransition(
  from: ControlReviewStatus,
  to: ControlReviewStatus,
): boolean {
  return (CONTROL_REVIEW_TRANSITIONS[from] ?? []).includes(to);
}

/**
 * Deterministic priority rules (LOW / MEDIUM / HIGH).
 *
 * Priority is a review-ordering aid derived from the CATEGORY's evidence
 * class at the moment the case is opened - never from a clock, deadline,
 * SLA or amount threshold, and never from an accusation:
 *
 *   HIGH     - the source evidence contradicts an enforced invariant
 *              (data integrity) or money recorded disagrees with money
 *              counted (reconciliation difference): highest review value.
 *   MEDIUM   - a measurable, factual difference exists on the record
 *              (item counts, payment facts) but the workflow still holds.
 *   LOW      - the record is selected for review without a measured
 *              difference (open work, document review, exceptions).
 *
 * Priority is stored at creation and never recomputed, so the ordering
 * an admin saw on day one is the ordering the history keeps.
 */
export const CONTROL_REVIEW_PRIORITIES = ['LOW', 'MEDIUM', 'HIGH'] as const;
export type ControlReviewPriority = (typeof CONTROL_REVIEW_PRIORITIES)[number];

export const CONTROL_REVIEW_PRIORITY_LABELS: Record<ControlReviewPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
};

export const CONTROL_REVIEW_PRIORITY_HINTS: Record<ControlReviewPriority, string> = {
  HIGH: 'Recorded facts contradict an enforced rule, or recorded money differs from counted money',
  MEDIUM: 'A measurable factual difference is recorded on the source record',
  LOW: 'Selected for review with no measured difference recorded',
};

/** Deterministic priority for a category - fixed rule per category, documented above. */
export const CONTROL_REVIEW_CATEGORY_PRIORITY: Record<ControlReviewCategory, ControlReviewPriority> =
  {
    ITEM_COUNT_DISCREPANCY: 'MEDIUM',
    OPEN_OR_STALLED_PROCESSING: 'LOW',
    TRANSACTION_WORKFLOW_INCONSISTENCY: 'HIGH',
    PAYMENT_COLLECTION_ISSUE: 'MEDIUM',
    CASH_RECONCILIATION_DIFFERENCE: 'HIGH',
    INVENTORY_ADJUSTMENT_CONCERN: 'MEDIUM',
    PURCHASE_OR_EXPENSE_REVIEW: 'LOW',
    OPERATIONAL_HANDOVER_ISSUE: 'LOW',
    PICKUP_DELIVERY_EXCEPTION: 'LOW',
  };

/** Priority for a category (server-computed; never accepted from a client). */
export function deriveReviewPriority(category: ControlReviewCategory): ControlReviewPriority {
  return CONTROL_REVIEW_CATEGORY_PRIORITY[category];
}

/** Ranking for deterministic ordering (lowest rank = reviewed first). */
export const CONTROL_REVIEW_PRIORITY_RANK: Record<ControlReviewPriority, number> = {
  HIGH: 0,
  MEDIUM: 1,
  LOW: 2,
};

/** Lifecycle actions an admin can perform (each maps to one endpoint). */
export const CONTROL_REVIEW_ACTIONS = [
  'START_REVIEW',
  'ASSIGN',
  'ADD_NOTE',
  'REQUEST_ACTION',
  'CONTINUE_REVIEW',
  'RESOLVE',
  'REOPEN',
] as const;
export type ControlReviewAction = (typeof CONTROL_REVIEW_ACTIONS)[number];

export const CONTROL_REVIEW_ACTION_LABELS: Record<ControlReviewAction, string> = {
  START_REVIEW: 'Start review',
  ASSIGN: 'Assign reviewer',
  ADD_NOTE: 'Add note',
  REQUEST_ACTION: 'Request corrective action',
  CONTINUE_REVIEW: 'Continue review',
  RESOLVE: 'Resolve',
  REOPEN: 'Reopen',
};
